/* eslint-disable ember/no-tracked-properties-from-args */
import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { fn } from "@ember/helper";
import { action } from "@ember/object";
import { service } from "@ember/service";
import DatePickerPast from "discourse/components/date-picker-past";
import { ajax } from "discourse/lib/ajax";
import { popupAjaxError } from "discourse/lib/ajax-error";
import { bind } from "discourse/lib/decorators";
import ComboBox from "discourse/select-kit/components/combo-box";
import { eq } from "discourse/truth-helpers";
import DButton from "discourse/ui-kit/d-button";
import DModal from "discourse/ui-kit/d-modal";
import dIcon from "discourse/ui-kit/helpers/d-icon";
import { i18n } from "discourse-i18n";

export default class RecalculateScoresForm extends Component {
  @service messageBus;

  @tracked updateRangeValue = 0;
  @tracked recalculateFromDate = "";
  @tracked remaining = this.args.model.recalculate_scores_remaining;
  @tracked status = "initial";

  updateRange = [
    {
      name: i18n("gamification.update_range.last_10_days"),
      value: 0,
      calculation: { count: 10, type: "days" },
    },
    {
      name: i18n("gamification.update_range.last_30_days"),
      value: 1,
      calculation: { count: 30, type: "days" },
    },
    {
      name: i18n("gamification.update_range.last_90_days"),
      value: 2,
      calculation: { count: 90, type: "days" },
    },
    {
      name: i18n("gamification.update_range.last_year"),
      value: 3,
      calculation: { count: 1, type: "year" },
    },
    { name: i18n("gamification.update_range.all_time"), value: 4 },
    { name: i18n("gamification.update_range.custom_date_range"), value: 5 },
  ];

  constructor() {
    super(...arguments);
    this.messageBus.subscribe("/recalculate_scores", this.onMessage);
  }

  willDestroy() {
    super.willDestroy(...arguments);
    this.messageBus.unsubscribe("/recalculate_scores", this.onMessage);
  }

  get remainingText() {
    return i18n("gamification.daily_update_scores_availability", {
      count: this.remaining,
    });
  }

  get applyDisabled() {
    const haveAvailability = this.args.model.recalculate_scores_remaining > 0;
    if (!haveAvailability || this.status !== "initial") {
      return true;
    }

    return (
      this.updateRangeValue === 5 &&
      this.recalculateFromDate <= moment().locale("en").utc().endOf("day")
    );
  }

  get dateRange() {
    if (this.updateRangeValue === 4) {
      return;
    }

    let today = moment().locale("en").utc().endOf("day");
    let pastDate = this.dateRangeToDate(this.updateRangeValue);
    return `${pastDate} - ${today.format(
      i18n("dates.long_with_year_no_time")
    )}`;
  }

  @bind
  onMessage(message) {
    if (message.success) {
      this.status = "complete";
      this.args.model.recalculate_scores_remaining = message.remaining;
      this.remaining = message.remaining;
    }
  }

  @bind
  dateRangeToDate(updateRangeValue) {
    if (updateRangeValue === 4) {
      return "2014-8-26";
    }

    if (updateRangeValue === 5) {
      return this.recalculateFromDate;
    }

    let today = moment().locale("en").utc().endOf("day");
    let updateRange = this.updateRange.find((obj) => {
      return obj.value === updateRangeValue;
    });
    let pastDate = today
      .clone()
      .subtract(updateRange.calculation.count, updateRange.calculation.type);

    return pastDate.format(i18n("dates.long_with_year_no_time"));
  }

  @action
  apply() {
    this.status = "loading";
    const data = {
      from_date: this.dateRangeToDate(this.updateRangeValue),
    };

    return ajax(`/admin/plugins/gamification/recalculate-scores.json`, {
      data,
      type: "PUT",
    }).catch(popupAjaxError);
  }

  <template>
    <DModal
      class="recalculate-scores-form-modal"
      @title={{i18n "gamification.recalculate"}}
    >
      <:body>
        {{#if (eq this.status "loading")}}
          <div class="recalculate-modal__status">
            <em>{{i18n "gamification.recalculating"}}</em>
          </div>
        {{else if (eq this.status "complete")}}
          <div class="recalculate-modal__status is-success">
            {{dIcon "check"}}
            {{i18n "gamification.completed"}}
          </div>
        {{else}}
          <form class="form-horizontal">
            <div class="input-group">
              <label>{{i18n "gamification.update_scores_help"}}</label>
              <ComboBox
                @content={{this.updateRange}}
                @id="update-range"
                @onChange={{fn (mut this.updateRangeValue)}}
                @value={{this.updateRangeValue}}
                @valueProperty="value"
              />

              {{#if (eq this.updateRangeValue 5)}}
                <div class="input-group -custom-range">
                  <label>{{i18n "gamification.custom_range_from"}}</label>
                  <DatePickerPast
                    class="date-input"
                    @id="custom-from-date"
                    @onSelect={{fn (mut this.recalculateFromDate)}}
                    @placeholder="yyyy-mm-dd"
                    @value={{this.recalculateFromDate}}
                  />
                </div>
              {{else}}
                <div class="recalculate-modal__date-range">
                  {{this.dateRange}}
                </div>
              {{/if}}
            </div>
          </form>
        {{/if}}
      </:body>

      <:footer>
        <DButton
          class="btn-primary"
          id="apply-section"
          @action={{this.apply}}
          @ariaLabel="gamification.apply"
          @disabled={{this.applyDisabled}}
          @label="gamification.apply"
        />
        <DButton
          class="btn-default"
          id="cancel-section"
          @action={{@closeModal}}
          @ariaLabel="gamification.cancel"
          @label={{if
            (eq this.status "complete")
            "gamification.close"
            "gamification.cancel"
          }}
        />

        <div class="recalculate-modal__footer-text">{{this.remainingText}}</div>
      </:footer>
    </DModal>
  </template>
}
